import {
    FormControl,
    FormHelperText,
    Icon,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    SelectProps,
    Tooltip,
} from '@mui/material';
import React from 'react';

import { GeneralConstants } from 'app/constants';
import { useOpposingPatterns } from 'app/hooks/use-opposing-patterns';
import { SeamPattern } from 'app/model';
import { SeamRequest } from 'app/model/seam-request';

type OpposingPatternSelectorProps = SelectProps<string | undefined> & {
    helperText?: string;
    variant?: 'filled' | 'outlined' | 'standard';
    size?: 'medium' | 'small';
    seamRequest: SeamRequest;
};

const LABEL_LENGTH = 20;

const patternLabel = (pattern: SeamPattern): string => {
    const data = pattern.data;

    if (data.descPattern) {
        return data.descPattern;
    }

    if (data.midPattern) {
        return data.midPattern;
    }

    if (data.mccPattern && data.mccPattern.length) {
        return data.mccPattern.join(', ');
    }

    if (data.cityPattern || data.statePattern || data.zipPattern) {
        return `${data.cityPattern ?? ''}, ${data.statePattern ?? ''} ${data.zipPattern ?? ''}`;
    }

    return '';
};

export const OpposingPatternSelector: React.FunctionComponent<OpposingPatternSelectorProps> = ({
    value,
    helperText,
    size = 'small',
    seamRequest,
    ...rest
}) => {
    const { patterns, isLoading } = useOpposingPatterns(seamRequest);

    return (
        <FormControl variant='outlined' size={size} className='my-2' style={{ width: '25em' }}>
            <InputLabel id='opposing-pattern-label' shrink>
                Opposing Pattern
            </InputLabel>
            <Select
                {...rest}
                displayEmpty
                value={value ?? ''}
                label='Opposing Pattern'
                disabled={isLoading}
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: '300px',
                        },
                    },
                }}
                labelId='opposing-pattern-label'
                startAdornment={
                    <InputAdornment position='start'>
                        <Tooltip title='This incurs lots of overhead and can slow down search.'>
                            <Icon color='info'>help_outline</Icon>
                        </Tooltip>
                    </InputAdornment>
                }
            >
                <MenuItem value=''>
                    <em>None</em>
                </MenuItem>
                {patterns.map((pattern, index) => (
                    <MenuItem key={pattern.id} value={pattern.id}>
                        {index + 1} - {patternLabel(pattern).substring(0, LABEL_LENGTH)} -{' '}
                        {pattern.id.substring(0, GeneralConstants.SHORT_ID_LENGTH)}
                    </MenuItem>
                ))}
            </Select>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    );
};
